import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISearchBy, ISearchByOpts } from '../interfaces/search';
import SearchCity from './search-city';

// Popular-cities illustrations (Hamburg / Berlin) are ready in
// `@/assets/images/cities` — re-enable the section below when testing.
// import { cityInitials, getCityIllustration } from '@/assets/images/cities';
// import { useExchangeStore } from '../store/store';

interface IProps {
  searchBy: ISearchBy;
  onSetSearchBy: (searchBy: ISearchByOpts) => void;
  onChangeModule: (moduleName: string) => void;
}

const RECENT_KEY = 'n4n.recentSearches';
// const MAX_SUGGESTED_CITIES = 3;

function readRecent(): Array<{ city: string; state: string; label: string }> {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, 4) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: { city: string; state: string; label: string }) {
  const existing = readRecent().filter(
    (item) => !(item.city === entry.city && item.state === entry.state)
  );
  localStorage.setItem(RECENT_KEY, JSON.stringify([entry, ...existing].slice(0, 4)));
}

const SearchRegions = ({ searchBy, onSetSearchBy, onChangeModule }: IProps) => {
  const { t } = useTranslation();
  // const exchanges = useExchangeStore((state) => state.exchanges);
  // const availableListingPlaces = useExchangeStore((state) => state.availableListingPlaces);
  const [displayValue, setDisplayValue] = useState('');
  const [recent, setRecent] = useState(readRecent);

  // Prefer cities that appear in the currently loaded listings, then fill from the
  // accumulated available-listing set — same source as the Stadt filter, max 3.
  // const suggestedCities = useMemo(() => {
  //   const frequency = new Map<string, number>();
  //   for (const exchange of exchanges) {
  //     const city = exchange.city?.trim();
  //     if (!city) {
  //       continue;
  //     }
  //     const state = exchange.state?.trim() || '';
  //     const key = `${city.toLowerCase()}|${state.toLowerCase()}`;
  //     frequency.set(key, (frequency.get(key) || 0) + 1);
  //   }
  //
  //   return [...availableListingPlaces]
  //     .sort((a, b) => {
  //       const keyA = `${a.city.toLowerCase()}|${a.state.toLowerCase()}`;
  //       const keyB = `${b.city.toLowerCase()}|${b.state.toLowerCase()}`;
  //       const freqDiff = (frequency.get(keyB) || 0) - (frequency.get(keyA) || 0);
  //       if (freqDiff !== 0) {
  //         return freqDiff;
  //       }
  //       const hasArtA = getCityIllustration(a.city) ? 1 : 0;
  //       const hasArtB = getCityIllustration(b.city) ? 1 : 0;
  //       if (hasArtB !== hasArtA) {
  //         return hasArtB - hasArtA;
  //       }
  //       return a.city.localeCompare(b.city, undefined, { sensitivity: 'base' });
  //     })
  //     .slice(0, MAX_SUGGESTED_CITIES);
  // }, [availableListingPlaces, exchanges]);

  useEffect(() => {
    if (searchBy.city && searchBy.state) {
      setDisplayValue(`${searchBy.city}, ${searchBy.state}`);
    } else if (searchBy.state) {
      setDisplayValue(searchBy.state);
    } else if (searchBy.city) {
      setDisplayValue(searchBy.city);
    } else {
      setDisplayValue('');
    }
  }, [searchBy.city, searchBy.state]);

  const handleDestinationChange = (value: string) => {
    setDisplayValue(value);
    if (!value || value.trim() === '') {
      onSetSearchBy({
        ...searchBy,
        city: '',
        state: '',
      });
    }
  };

  const applyDestination = (selectedCity: string, selectedState: string) => {
    const label =
      selectedCity && selectedState
        ? `${selectedCity}, ${selectedState}`
        : selectedState || selectedCity;

    onSetSearchBy({
      ...searchBy,
      city: selectedCity,
      state: selectedState,
    });
    setDisplayValue(label);

    if (label) {
      pushRecent({ city: selectedCity, state: selectedState, label });
      setRecent(readRecent());
    }
    onChangeModule('startDate');
  };

  return (
    <section className="search-regions">
      <SearchCity
        stateValue=""
        value={displayValue}
        onChange={handleDestinationChange}
        handleSubmit={applyDestination}
      />

      {recent.length > 0 && (
        <div className="search-regions__section">
          <h5>{t('search-regions-recent-title')}</h5>
          <div className="search-regions__chips">
            {recent.map((item) => (
              <button
                key={`${item.city}-${item.state}`}
                type="button"
                onClick={() => applyDestination(item.city, item.state)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Popular cities temporarily disabled — Hamburg/Berlin SVGs + styles are ready.
      {suggestedCities.length > 0 && (
        <div className="search-regions__section">
          <h5>{t('search-regions-popular-title')}</h5>
          <div
            className="search-regions__popular"
            style={{
              gridTemplateColumns: `repeat(${suggestedCities.length}, minmax(0, 1fr))`,
            }}
          >
            {suggestedCities.map((dest) => {
              const illustration = getCityIllustration(dest.city);
              return (
                <button
                  key={`popular-${dest.city}-${dest.state}`}
                  type="button"
                  className="search-regions__card"
                  onClick={() => applyDestination(dest.city, dest.state)}
                >
                  <div
                    className={`search-regions__card-media${
                      illustration ? ' search-regions__card-media--illustrated' : ''
                    }`}
                    aria-hidden="true"
                  >
                    {illustration ? (
                      <img
                        src={illustration}
                        alt=""
                        className="search-regions__card-illustration"
                      />
                    ) : (
                      <span className="search-regions__card-initials">
                        {cityInitials(dest.city)}
                      </span>
                    )}
                  </div>
                  <span className="search-regions__card-label">{dest.city}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      */}
    </section>
  );
};

export default SearchRegions;
